import { type RedeemedCoupon } from "@/hooks/useRewards";

interface Props {
  coupon: RedeemedCoupon;
  onCopy: (code: string) => void;
  copiedCode: string | null;
}

export default function CouponCard({ coupon, onCopy, copiedCode }: Props) {
  const isActive = coupon.status === "active";
  const isUsed = coupon.status === "used";
  const isExpired = coupon.status === "expired";

  const expiresDate = new Date(coupon.expiresAt).toLocaleDateString("ar-SA", {
    year: "numeric", month: "short", day: "numeric",
  });
  const createdDate = new Date(coupon.createdAt).toLocaleDateString("ar-SA", {
    year: "numeric", month: "short", day: "numeric",
  });

  const daysLeft = isActive
    ? Math.max(0, Math.ceil((new Date(coupon.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;

  const isCopied = copiedCode === coupon.couponCode;

  return (
    <div className={`relative rounded-2xl overflow-hidden border transition-all ${
      isActive
        ? "bg-gradient-to-br from-green-900/20 to-gray-900/60 border-green-400/25"
        : "bg-[#111] border-white/8 opacity-60"
    }`}>
      {/* Dashed divider (ticket style) */}
      <div className="absolute top-0 bottom-0 right-[72px] border-r border-dashed border-white/10 pointer-events-none"></div>

      <div className="flex items-stretch">
        {/* Left stub: discount value */}
        <div className={`w-[72px] flex-shrink-0 flex flex-col items-center justify-center p-3 ${
          isActive ? "bg-green-400/10" : "bg-white/3"
        }`}>
          <p className={`text-2xl font-black leading-none ${isActive ? "text-green-400" : "text-gray-600"}`}
            style={{ fontFamily: "'Cairo', sans-serif" }}>
            {coupon.discountPercent}%
          </p>
          <p className={`text-[10px] mt-0.5 ${isActive ? "text-green-400/70" : "text-gray-600"}`}
            style={{ fontFamily: "'Cairo', sans-serif" }}>
            خصم
          </p>
        </div>

        {/* Right: details */}
        <div className="flex-1 p-4">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              {/* Status badge */}
              <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full mb-1.5 ${
                isActive  ? "bg-green-400/15 text-green-400" :
                isUsed    ? "bg-gray-700 text-gray-400" :
                            "bg-red-400/15 text-red-400"
              }`} style={{ fontFamily: "'Cairo', sans-serif" }}>
                <span className={`w-1.5 h-1.5 rounded-full ${
                  isActive ? "bg-green-400 animate-pulse" : isUsed ? "bg-gray-500" : "bg-red-400"
                }`}></span>
                {isActive ? "نشط" : isUsed ? "مستخدم" : "منتهي"}
              </span>

              {/* Coupon code */}
              <p className={`font-mono text-sm font-black tracking-widest ${isActive ? "text-white" : "text-gray-500"}`}>
                {coupon.couponCode}
              </p>
            </div>

            {/* Copy button */}
            {isActive && (
              <button
                onClick={() => onCopy(coupon.couponCode)}
                className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-xl transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${
                  isCopied
                    ? "bg-green-400 text-black"
                    : "bg-white/10 hover:bg-white/20 text-gray-300"
                }`}
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                <i className={`text-xs ${isCopied ? "ri-check-line" : "ri-file-copy-line"}`}></i>
                {isCopied ? "تم النسخ" : "نسخ"}
              </button>
            )}
          </div>

          {/* Meta info */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className="flex items-center gap-1 text-[11px] text-gray-500" style={{ fontFamily: "'Cairo', sans-serif" }}>
              <i className="ri-coin-line text-amber-600 text-xs"></i>
              {coupon.pointsUsed} نقطة
            </span>
            <span className="flex items-center gap-1 text-[11px] text-gray-500" style={{ fontFamily: "'Cairo', sans-serif" }}>
              <i className="ri-calendar-line text-xs"></i>
              صدر: {createdDate}
            </span>
            {isActive && (
              <span className={`flex items-center gap-1 text-[11px] font-bold ${daysLeft <= 5 ? "text-red-400" : "text-gray-500"}`}
                style={{ fontFamily: "'Cairo', sans-serif" }}>
                <i className={`ri-time-line text-xs ${daysLeft <= 5 ? "text-red-400" : ""}`}></i>
                {daysLeft <= 5 ? `ينتهي خلال ${daysLeft} أيام!` : `ينتهي: ${expiresDate}`}
              </span>
            )}
            {isUsed && coupon.usedAt && (
              <span className="flex items-center gap-1 text-[11px] text-gray-600" style={{ fontFamily: "'Cairo', sans-serif" }}>
                <i className="ri-check-double-line text-xs"></i>
                استُخدم: {new Date(coupon.usedAt).toLocaleDateString("ar-SA", { month: "short", day: "numeric" })}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Urgent expiry warning */}
      {isActive && daysLeft <= 3 && daysLeft > 0 && (
        <div className="bg-red-400/10 border-t border-red-400/20 px-4 py-2 flex items-center gap-2">
          <i className="ri-alarm-warning-line text-red-400 text-xs"></i>
          <p className="text-red-400 text-[11px] font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
            تنبيه: الكوبون ينتهي خلال {daysLeft} {daysLeft === 1 ? "يوم" : "أيام"} — استخدمه الآن!
          </p>
        </div>
      )}
    </div>
  );
}
